import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { holdsProductFor, screenForPath } from '@/app/navigation/productAccess';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/session';
import { AppLoader } from '@/shared/components/feedback/AppLoader';

import { ProductNotAssigned } from './ProductNotAssigned';

/**
 * Keeps somebody off a product screen they have not been assigned.
 *
 * <h2>The fifth guard, and it belongs in the routing table like the other four</h2>
 *
 * `ProtectedRoute` keeps a signed-out visitor off the signed-in screens, `SuperAdminRoute`
 * keeps a tenant admin off the operator's, `GuestRoute` keeps a signed-in person off the
 * sign-in form, and `SignOutFirstRoute` stands in front of `/reset-password`. This one
 * keeps a signed-in person off a product that is not theirs.
 *
 * <p>Declared in `app/router` rather than inside each screen, so which side a route is on is
 * visible where routes are read — and so a product screen added later is guarded by where it
 * is put rather than by somebody remembering.
 *
 * <h2>It is a courtesy. The server is the boundary.</h2>
 *
 * Since 3 October 2026 every product endpoint answers **403 `product-not-assigned`** to a
 * caller who holds nothing for it (backend `product-privileges.md` PP-28), so a screen
 * reached past this guard would render and then fail to load anything. What this buys is
 * that it says **why** instead of leaving each screen to surface its own error — the same
 * division as the hidden menu row, which is bypassed by typing the URL.
 *
 * <p>So it must never be the only thing standing there. A guard that is removed, or a route
 * added outside it, costs an explanation and not a leak.
 *
 * <h2>It shows a screen rather than redirecting</h2>
 *
 * Bouncing somebody to the dashboard leaves them wondering whether they mistyped — and
 * silently sends away the person most likely to need telling, since the usual way to arrive
 * here is a link a colleague sent who *does* hold the product. The message names what is
 * missing and who to ask.
 *
 * <p>It is also not a 404. The screen exists; the server answers 403 and not 404, and a page
 * saying "there is nothing here" would contradict it and send somebody looking for a typo.
 * Hiding existence is the right answer only where existence is itself the secret — which a
 * product in a published catalogue is not.
 *
 * <h2>Which product it demands comes from the screen registry</h2>
 *
 * `screenForPath` looks the route up in `SCREENS`, so the products behind a screen are
 * declared in exactly one place and the rail and this guard cannot disagree about them.
 *
 * <p>It read `NAV_ITEMS` until 8 October 2026, which tied this guard to the sidebar — hiding
 * a row removed the check in front of its screen. Three screens have no row today and all
 * three are still guarded. A path that is not a registered screen — the assistant, a
 * collection, profile, billing — names no product and passes.
 */
export function ProductRoute() {
  const { user, status } = useAuth();
  const { pathname } = useLocation();

  if (status === 'signed-out') {
    // ProtectedRoute above already handles this for nested routes; the check is here too so
    // this component is safe to mount anywhere. One definition of signed-out, shared.
    return <Navigate to={ROUTES.signIn} replace />;
  }

  if (status === 'unknown' || !user) {
    // The products are unknown, not empty. Rendering the refusal here would accuse somebody
    // of holding nothing while the question is still in flight -- the same reason the rail
    // waits rather than guessing.
    return <AppLoader />;
  }

  const screen = screenForPath(pathname);
  // The key set of `products` IS what this person may open -- backend PP-31.
  const productKeys = new Set(Object.keys(user.products));

  if (screen && !holdsProductFor(screen, productKeys)) {
    // The screen rather than its title, so the refusal can draw the product's own icon -- a
    // generic padlock is the same picture on all six and says only "no".
    return <ProductNotAssigned screen={screen} />;
  }

  return <Outlet />;
}
